import { timing, variantForLevel } from './params';
const FEEDBACK_TAP_DELAY_MS = 200;
const DIGIT_POOL = 10;
function sampleNoConsecutiveRepeat(n, poolSize) {
    const out = [];
    let last = -1;
    for (let i = 0; i < n; i += 1) {
        let v = Math.floor(Math.random() * poolSize);
        // Évite répétition immédiate (ex: 3,3,5) pour minimiser l'effet de chunking trivial.
        if (v === last) {
            v = (v + 1) % poolSize;
        }
        out.push(v);
        last = v;
    }
    return out;
}
export function makeStimulus(trialIdx, value, level) {
    const variant = variantForLevel(level);
    const spanLength = Math.max(2, Math.min(Math.round(value), 12));
    const items = sampleNoConsecutiveRepeat(spanLength, DIGIT_POOL);
    return {
        trialIdx,
        spanLength,
        items,
        itemDurationMs: variant.itemDurationMs,
    };
}
export function createDigitSpanBackwardAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        stim: null,
        currentItemIdx: -1,
        picked: [],
        feedback: null,
        level,
        variant: variantForLevel(level),
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function settle(picked) {
        if (!resolver || !state.stim)
            return;
        const truthReversed = state.stim.items.slice().reverse();
        let hits = 0;
        const n = Math.min(picked.length, truthReversed.length);
        for (let i = 0; i < n; i += 1) {
            if (picked[i] === truthReversed[i])
                hits += 1;
        }
        const correct = picked.length === truthReversed.length &&
            picked.every((p, i) => p === truthReversed[i]);
        const rt = Date.now() - trialStart;
        const r = resolver;
        resolver = null;
        rejecter = null;
        r({
            picked: [...picked],
            truthReversed: [...truthReversed],
            hits,
            correct,
            reactionTimeMs: rt,
        });
    }
    function presentItem(stim, idx) {
        if (idx >= stim.items.length) {
            update({ subPhase: 'awaiting_input', currentItemIdx: -1 });
            trialStart = Date.now();
            return;
        }
        update({ subPhase: 'presenting', currentItemIdx: idx });
        timers.push(setTimeout(() => {
            // Gap silencieux entre items.
            update({ currentItemIdx: -1 });
            timers.push(setTimeout(() => {
                presentItem(stim, idx + 1);
            }, timing.gapMs));
        }, stim.itemDurationMs));
    }
    return {
        mount() {
            // Idempotent, rendu déclaratif côté React.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({
                    subPhase: 'fixation',
                    stim,
                    currentItemIdx: -1,
                    picked: [],
                    feedback: null,
                });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'pre_sequence' });
                    timers.push(setTimeout(() => {
                        presentItem(stim, 0);
                    }, timing.preSequenceMs));
                }, timing.fixationMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, timing.feedbackMs));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handlePick(digit) {
            if (state.subPhase !== 'awaiting_input' || !state.stim)
                return;
            const newPicked = [...state.picked, digit];
            update({ picked: newPicked });
            if (newPicked.length >= state.stim.spanLength) {
                timers.push(setTimeout(() => {
                    settle(newPicked);
                }, FEEDBACK_TAP_DELAY_MS));
            }
        },
        handleClearLast() {
            if (state.subPhase !== 'awaiting_input')
                return;
            if (state.picked.length === 0)
                return;
            update({ picked: state.picked.slice(0, -1) });
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
//# sourceMappingURL=adapter.js.map